/**
 * ExportBundle — what a report looks like once its data has been worked out, for the PDF and Excel writers.
 *
 * The studio resolves every visual of a report (tables, pivots, charts, the map snapshot, text) into this plain,
 * already-computed shape; the writers never see datasets, filters or aggregations. Percent values are kept in
 * percent units (82.9 means 82.9 %), as the catchment tables show them.
 */

export type CellValue = string | number | null;

/** How a value is shown: `percent` values are percent units (82.9 → "%82.9" / 82.9 %). */
export type ValueFormat = 'number' | 'integer' | 'percent' | 'text';

export interface BundleColumn {
  key: string;
  label: string;
  format: ValueFormat;
}

export interface BundleTable {
  id: string;
  title: string;
  subtitle?: string;
  columns: BundleColumn[];
  /** rows[r][c] belongs to columns[c]. */
  rows: CellValue[][];
  /** Indexes of rows that are totals (drawn bold). */
  totalRows?: number[];
}

export type ExportChartType =
  | 'column' | 'stackedColumn' | 'percentColumn'
  | 'bar' | 'stackedBar'
  | 'line' | 'area'
  | 'pie' | 'donut'
  | 'scatter'
  | 'radar'
  | 'kpi';

export interface BundleSeries {
  name: string;
  values: (number | null)[];
  /** #rrggbb */
  color: string;
}

export interface BundleChart {
  id: string;
  title: string;
  type: ExportChartType;
  /** One label per point; for scatter the X values as text (scatterX holds the numbers). */
  categories: string[];
  series: BundleSeries[];
  format: ValueFormat;
  categoryLabel: string;
  valueLabel?: string;
  showLabels?: boolean;
  showLegend?: boolean;
  /** Scatter only: the numeric X of each point. */
  scatterX?: (number | null)[];
  /** KPI only. */
  kpi?: { value: number | null; label: string; format: ValueFormat; caption?: string };
  /** The table whose data the chart shows, when it is one of the report's tables (Excel puts the chart beside it). */
  sourceTableId?: string;
}

export interface BundleItem {
  id: string;
  kind: 'table' | 'chart' | 'map' | 'text';
  /** Where it sits on the studio canvas, in canvas pixels: the writers keep the reading order and the proportions. */
  x: number;
  y: number;
  w: number;
  h: number;
  table?: BundleTable;
  chart?: BundleChart;
  /** map: a JPEG or PNG data URL; caption: what was on the map. */
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  caption?: string;
  text?: string;
}

export interface BundleLabels {
  page: string;          // "Page {{page}} of {{total}}"
  generated: string;     // "Generated {{date}}"
  continued: string;     // "(continued)"
  columnsPart: string;   // "Columns {{from}}–{{to}} of {{total}}"
  transposed: string;    // "Rows and columns swapped to fit the page"
  contents: string;      // "Contents" (Excel first sheet name / heading)
  chartData: string;     // "Chart data" (Excel helper sheet)
  map: string;           // "Map"
  noData: string;        // "No data"
}

export interface ExportBundle {
  title: string;
  subtitle?: string;
  generatedAt: Date;
  /** BCP-47, for number and date formatting. */
  locale: string;
  /** The company's logo as a data URL (PNG/JPEG), shown in the header of the outputs. */
  companyLogo?: string;
  orientation: 'portrait' | 'landscape';
  /** The studio canvas' page width in pixels: an item as wide as this fills the printable width. */
  canvasWidth: number;
  items: BundleItem[];
  labels: BundleLabels;
}

/** "{{a}}" placeholders. */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, k) => (values[k] != null ? String(values[k]) : ''));
}

/** A value as text, the way the studio shows it. */
export function formatValue(v: CellValue, format: ValueFormat, locale: string): string {
  if (v == null || v === '') return '';
  if (typeof v === 'string') return v;
  if (!Number.isFinite(v)) return '';
  switch (format) {
    case 'percent':
    {
      const n = v.toLocaleString(locale, { maximumFractionDigits: 1, minimumFractionDigits: 0 });
      // Turkish writes the sign first (%82,9), the others after (82.9 %, 82,9 %).
      return locale.toLowerCase().startsWith('tr') ? '%' + n : locale.toLowerCase().startsWith('en') ? n + '%' : n + ' %';
    }
    case 'integer':
      return Math.round(v).toLocaleString(locale);
    case 'number':
      return v.toLocaleString(locale, { maximumFractionDigits: Number.isInteger(v) ? 0 : 2 });
    default:
      return String(v);
  }
}
